import type { Permisos } from '@/types/roles'
import { toast } from 'sonner'

export class PermisoError extends Error {
  constructor(permiso: string) {
    super(`Sin permiso: ${permiso}`)
    this.name = 'PermisoError'
  }
}

/** Error cuyo texto ya está escrito para el usuario: `handleMutationError` lo muestra tal cual. */
export class ErrorParaElUsuario extends Error {
  constructor(mensaje: string, causa?: unknown) {
    super(mensaje, { cause: causa })
    this.name = 'ErrorParaElUsuario'
  }
}

export const assertPermiso = (puede: Partial<Permisos>, permiso: keyof Permisos) => {
  if (!puede[permiso]) throw new PermisoError(permiso)
}

/** Lo mínimo que hace falta para decidir si una tarea es propia. */
export type TareaPropiedad = {
  asignado_a?: string | null
  created_by?: string | null
}

/**
 * Propia = asignada a mí O creada por mí (decidido el 10/09, D-18).
 *
 * Responde solo de quién es la tarea; el permiso lo decide `puedeTocarTarea`. Están separadas
 * porque el interruptor "Míos / Todo el estudio" necesita la pertenencia SIN el permiso: un
 * propietario puede tocar cualquier tarea y aun así "Míos" tiene que dejar afuera las ajenas
 * (ORR-398).
 */
export const tareaEsPropia = (
  tarea: TareaPropiedad | null | undefined,
  emailUsuario: string | null | undefined
): boolean => {
  // Sin tarea no hay verificación posible, y la ausencia de dato no es pertenencia: el helper que
  // la busca devuelve null cuando la lectura falla, así que un `tarea &&` acá fallaría abierto.
  if (!tarea || !emailUsuario) return false
  // Los dos lados se normalizan. `emailUsuario` viene en minúsculas de usuarios_brainhub, pero
  // `created_by` se guarda con el email crudo de Auth0 (useTareas.ts:221), así que comparar contra
  // un solo lado falla en una dirección o en la otra.
  const propio = emailUsuario.toLowerCase()
  return (
    tarea.asignado_a?.toLowerCase() === propio || tarea.created_by?.toLowerCase() === propio
  )
}

/**
 * "Míos" en las listas de tareas: la que tengo asignada, sin mirar quién la creó.
 *
 * Distinta de `tareaEsPropia`, que decide el permiso de edición y sí incluye al creador: cambiar
 * esta no cambia quién puede tocar una tarea.
 */
export const tareaAsignadaAMi = (
  tarea: TareaPropiedad | null | undefined,
  emailUsuario: string | null | undefined
): boolean => {
  if (!tarea || !emailUsuario) return false
  return tarea.asignado_a?.toLowerCase() === emailUsuario.toLowerCase()
}

/**
 * ORR-381: la única definición de "puedo tocar esta tarea" — editarla o cambiarle el estado.
 * Antes vivía copiada en tres lugares con tres formas distintas, y por eso divergieron.
 */
export const puedeTocarTarea = (
  tarea: TareaPropiedad | null | undefined,
  puede: Partial<Permisos>,
  emailUsuario: string | null | undefined
): boolean => {
  if (puede.editarTarea) return true
  if (!puede.editarTareaPropia) return false
  return tareaEsPropia(tarea, emailUsuario)
}

/**
 * Devuelve true si el error es una negación de permiso: un `PermisoError` del front o un 42501 de la base.
 * La base devuelve 42501 cuando una policy RLS, un GRANT o `exigir_permiso` niegan la operación. No dice qué
 * permiso falta.
 */
export const esErrorSinPermiso = (error: unknown): boolean =>
  error instanceof PermisoError ||
  (typeof error === 'object' && error !== null && (error as { code?: unknown }).code === '42501')

/** Texto de una consulta que falló: dice si falta el permiso o si el dato no cargó. */
export const mensajeErrorDeCarga = (error: unknown): string =>
  esErrorSinPermiso(error) ? 'Sin permiso para ver estos datos.' : 'No se pudo cargar el dato.'

/**
 * Muestra el toast de error de una mutación y lo registra en consola.
 * Un `PermisoError` del front muestra que falta un permiso. Un `ErrorParaElUsuario` muestra su
 * texto. Un 42501 de la base muestra que la operación no está permitida, sin nombrar el permiso.
 * Cualquier otro error muestra `defaultMessage`.
 */
export const handleMutationError = (error: unknown, defaultMessage: string) => {
  if (error instanceof PermisoError) {
    toast.error('No tenés permisos para realizar esta acción')
  } else if (error instanceof ErrorParaElUsuario) {
    toast.error(error.message)
  } else if (esErrorSinPermiso(error)) {
    toast.error('No tenés permiso para hacer esto. Si creés que deberías, pedíselo al administrador del estudio.')
  } else {
    toast.error(defaultMessage)
  }
  console.error(defaultMessage, error)
}
